/**
 * Referral.tsx — Complete user referral page with:
 *   - Total Sales stat card (raw unweighted sum, unlimited depth)
 *   - Level progress bar that RESETS to 0 XRP at each new tier
 *   - Heat map thresholds driven by admin settings (heat_top_threshold / heat_mid_threshold)
 *   - 5-filter tree system (depth, active, deposit, contribution, heat tier)
 *   - Tree / Breakdown sub-tabs
 *   - All mobile scroll fixes
 */
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import Header from "../../components/Layout/Header";
import BottomNav from "../../components/Layout/BottomNav";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import Alert from "../../components/UI/Alert";
import Modal from "../../components/UI/Modal";
import globalStyle from "../../styles/globalStyle";
import referralService from "../../services/referralService";
import { formatXRP } from "../../utils/formatters";
import {
  UserGroupIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  UsersIcon,
  TrophyIcon,
  CheckCircleIcon,
  StarIcon,
  InformationCircleIcon,
  BanknotesIcon,
  CircleStackIcon,
  UserPlusIcon,
  PresentationChartLineIcon,
  GiftIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  FireIcon,
  AdjustmentsHorizontalIcon,
  ArrowTrendingUpIcon,
} from "@heroicons/react/24/outline";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReferralStats {
  referral_code: string;
  referral_link: string;
  total_referrals: number;
  active_referrals: number;
  team_volume: number;
  total_sales: number; // NEW: raw unweighted downline deposits
  total_earnings: number;
  today_earnings: number;
  week_earnings: number;
  month_earnings: number;
  user_level: number;
  level_name: string;
  username?: string;
  name?: string;
  xrp_balance?: number;
  /**
   * level_progress now uses RESET logic:
   *   progress_volume = teamVolume - currentLevelThreshold (resets to 0 at each tier)
   *   needed_volume   = nextLevelThreshold - currentLevelThreshold
   *   progress_pct    = 0–100 within the current tier band
   */
  level_progress: {
    current_level: number;
    max_level: number;
    at_max?: boolean;
    progress_volume: number;
    needed_volume: number;
    progress_pct: number;
    current_threshold: number;
    next_threshold: number | null;
    // legacy fallback fields
    team_volume?: number;
    next_level_requirements?: { min_volume: number } | null;
    current_level_requirements?: { min_volume: number };
  };
  referral_settings: {
    levels: number;
    commission_levels?: number;
    volume_depth_levels?: number;
    depth_rates?: Record<number, number>;
    rates: Record<number, number>;
    depth_weights?: Record<number, number>;
    floor_depth_weight?: number;
    floor_depth?: number;
    level_bonuses: Record<number, number>;
    level_names: Record<number, string>;
    level_requirements: Record<number, { min_volume: number }>;
    signup_bonus: number;
    daily_commission_limit: number;
    monthly_commission_limit: number;
    first_place_bonus: number;
    second_place_bonus: number;
    third_place_bonus: number;
    payout_schedule: string;
    is_active: boolean;
    heat_top_threshold?: number;
    heat_mid_threshold?: number;
  };
}

interface ReferralTreeNode {
  id: number;
  username: string;
  name: string;
  depth: number;
  commission_rate?: number;
  commission_pct?: number;
  volume_weight?: number;
  volume_weight_pct?: number;
  first_deposit: number;
  total_deposits: number;
  weighted_contribution?: number;
  team_volume: number;
  xrp_balance: number;
  joined_at: string;
  is_active: boolean;
  children: ReferralTreeNode[];
}

interface VolumeBreakdownRow {
  members: number;
  raw_deposits: number;
  weight: number;
  weighted_volume: number;
  depth_label?: string;
}

interface TreeFilters {
  depthMax: number | null;
  activeOnly: boolean;
  hasDeposit: boolean | null;
  minContrib: number;
  heatTier: "all" | "top" | "mid" | "dormant";
  heatMode: boolean;
}

// ─── Palettes ─────────────────────────────────────────────────────────────────

const DEPTH_PALETTE: Record<
  number,
  { border: string; glow: string; text: string; bg: string }
> = {
  0: {
    border: "#818cf8",
    glow: "rgba(129,140,248,0.30)",
    text: "#c7d2fe",
    bg: "rgba(99,102,241,0.12)",
  },
  1: {
    border: "#60a5fa",
    glow: "rgba(96,165,250,0.25)",
    text: "#93c5fd",
    bg: "rgba(59,130,246,0.10)",
  },
  2: {
    border: "#4ade80",
    glow: "rgba(74,222,128,0.25)",
    text: "#86efac",
    bg: "rgba(34,197,94,0.10)",
  },
  3: {
    border: "#fbbf24",
    glow: "rgba(251,191,36,0.25)",
    text: "#fde68a",
    bg: "rgba(245,158,11,0.10)",
  },
  4: {
    border: "#fb923c",
    glow: "rgba(251,146,60,0.25)",
    text: "#fed7aa",
    bg: "rgba(249,115,22,0.10)",
  },
  5: {
    border: "#c084fc",
    glow: "rgba(192,132,252,0.25)",
    text: "#e9d5ff",
    bg: "rgba(168,85,247,0.10)",
  },
  6: {
    border: "#f472b6",
    glow: "rgba(244,114,182,0.25)",
    text: "#fbcfe8",
    bg: "rgba(236,72,153,0.10)",
  },
  7: {
    border: "#34d399",
    glow: "rgba(52,211,153,0.25)",
    text: "#a7f3d0",
    bg: "rgba(16,185,129,0.10)",
  },
  8: {
    border: "#a78bfa",
    glow: "rgba(167,139,250,0.25)",
    text: "#ddd6fe",
    bg: "rgba(139,92,246,0.10)",
  },
  9: {
    border: "#f87171",
    glow: "rgba(248,113,113,0.25)",
    text: "#fecaca",
    bg: "rgba(239,68,68,0.10)",
  },
  10: {
    border: "#38bdf8",
    glow: "rgba(56,189,248,0.25)",
    text: "#bae6fd",
    bg: "rgba(14,165,233,0.10)",
  },
};
const dp = (d: number) => DEPTH_PALETTE[Math.min(d, 10)] ?? DEPTH_PALETTE[1];

const HEAT_PALETTE = {
  top: {
    border: "#f97316",
    glow: "rgba(249,115,22,0.4)",
    text: "#fed7aa",
    bg: "rgba(249,115,22,0.15)",
    label: "🔥 Top",
    emoji: "🔥",
  },
  mid: {
    border: "#38bdf8",
    glow: "rgba(56,189,248,0.3)",
    text: "#bae6fd",
    bg: "rgba(14,165,233,0.12)",
    label: "💧 Mid",
    emoji: "💧",
  },
  dormant: {
    border: "#475569",
    glow: "rgba(71,85,105,0.3)",
    text: "#94a3b8",
    bg: "rgba(71,85,105,0.12)",
    label: "💤 Dormant",
    emoji: "💤",
  },
};
type HeatTier = "top" | "mid" | "dormant";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getDepthRates(s: ReferralStats): Record<number, number> {
  const rates =
    s.referral_settings?.depth_rates ?? s.referral_settings?.rates ?? {};
  const max =
    s.referral_settings?.commission_levels ?? s.referral_settings?.levels ?? 5;
  const out: Record<number, number> = {};
  for (let i = 1; i <= max; i++) out[i] = rates[i] || 0;
  return out;
}

function getDepthWeights(s: ReferralStats): Record<number, number> {
  const weights = s.referral_settings?.depth_weights ?? {};
  const max =
    s.referral_settings?.volume_depth_levels ??
    s.referral_settings?.levels ??
    10;
  const out: Record<number, number> = {};
  for (let i = 1; i <= max; i++) out[i] = weights[i] ?? 0;
  return out;
}

function getFloorWeight(s: ReferralStats): number {
  if (s.referral_settings?.floor_depth_weight !== undefined)
    return s.referral_settings.floor_depth_weight;
  const weights = s.referral_settings?.depth_weights ?? {};
  const entries = Object.entries(weights).sort(
    ([a], [b]) => Number(a) - Number(b),
  );
  if (!entries.length) return 0;
  return Number(entries[entries.length - 1][1]);
}

function weightForDepth(
  depth: number,
  depthWeights: Record<number, number>,
  floorWeight: number,
): number {
  return depthWeights[depth] ?? floorWeight;
}

function collectAllIds(
  nodes: ReferralTreeNode[],
  ids = new Set<number>(),
): Set<number> {
  for (const n of nodes) {
    ids.add(n.id);
    if (n.children?.length) collectAllIds(n.children, ids);
  }
  return ids;
}

function flattenTree(nodes: ReferralTreeNode[]): ReferralTreeNode[] {
  const out: ReferralTreeNode[] = [];
  const walk = (ns: ReferralTreeNode[]) =>
    ns.forEach((n) => {
      out.push(n);
      walk(n.children ?? []);
    });
  walk(nodes);
  return out;
}

function buildHeatMap(
  allNodes: ReferralTreeNode[],
  topThreshold = 0.2,
  midThreshold = 0.7,
): Map<number, HeatTier> {
  const sorted = [...allNodes]
    .filter((n) => n.id !== -1)
    .sort(
      (a, b) => (b.weighted_contribution ?? 0) - (a.weighted_contribution ?? 0),
    );
  const total = sorted.length;
  const map = new Map<number, HeatTier>();
  sorted.forEach((n, i) => {
    const pct = total > 0 ? i / total : 1;
    map.set(
      n.id,
      pct < topThreshold ? "top" : pct < midThreshold ? "mid" : "dormant",
    );
  });
  return map;
}

const lvlBadgeClass = (level: number) => {
  if (level === 0) return "bg-gray-900 text-gray-500 border-gray-700";
  return (
    (
      {
        1: "bg-blue-900 text-blue-300 border-blue-700",
        2: "bg-amber-900 text-amber-300 border-amber-700",
        3: "bg-gray-700 text-gray-300 border-gray-600",
        4: "bg-yellow-900 text-yellow-300 border-yellow-700",
        5: "bg-purple-900 text-purple-300 border-purple-700",
      } as Record<number, string>
    )[level] ?? "bg-gray-800 text-gray-400 border-gray-700"
  );
};

const sw: React.CSSProperties = {
  overflowX: "auto",
  WebkitOverflowScrolling: "touch" as any,
};
const nw: React.CSSProperties = { whiteSpace: "nowrap" };

// ─── Loading ──────────────────────────────────────────────────────────────────

const LoadingScreen: React.FC<{ message?: string }> = ({
  message = "Loading...",
}) => (
  <div className="w-full h-64 flex flex-col gap-2 items-center justify-center">
    <svg className={globalStyle.loadingSpinner} viewBox="0 0 24 24">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
        fill="none"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
    <p className="mt-2 text-gray-400 text-sm">{message}</p>
  </div>
);

// ─── Tree Components ──────────────────────────────────────────────────────────

const COL_GAP = 52;
const ROW_GAP = 10;

const TreeCard: React.FC<{
  node: ReferralTreeNode;
  isRoot?: boolean;
  highlighted?: boolean;
  heatMode: boolean;
  heatTier?: HeatTier;
  onSelect: (n: ReferralTreeNode) => void;
  cardRef: (el: HTMLDivElement | null) => void;
}> = ({
  node,
  isRoot = false,
  highlighted,
  heatMode,
  heatTier,
  onSelect,
  cardRef,
}) => {
  const depthPal = dp(isRoot ? 0 : node.depth);
  const heatPal = heatTier ? HEAT_PALETTE[heatTier] : null;
  const pal = heatMode && heatPal && !isRoot ? heatPal : depthPal;
  return (
    <div
      ref={cardRef}
      onClick={() => !isRoot && onSelect(node)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: isRoot ? "6px 12px" : "4px 10px",
        borderRadius: 99,
        border: `1.5px solid ${highlighted ? "#facc15" : pal.border}`,
        background: highlighted
          ? "linear-gradient(90deg,#1c1709,rgba(250,204,21,0.15))"
          : `linear-gradient(90deg,#0f172a,${pal.bg})`,
        boxShadow: highlighted
          ? "0 0 10px rgba(250,204,21,0.4)"
          : `0 0 6px ${pal.glow}`,
        cursor: isRoot ? "default" : "pointer",
        userSelect: "none",
        WebkitTapHighlightColor: "transparent",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: node.is_active ? "#22c55e" : "#6b7280",
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontSize: isRoot ? 12 : 11,
          fontWeight: 700,
          color: "#f1f5f9",
          maxWidth: 100,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {node.username}
      </span>
      {(!heatMode || isRoot) && (
        <span
          style={{
            fontSize: 8,
            fontWeight: 800,
            color: pal.text,
            background: "rgba(0,0,0,0.4)",
            border: `1px solid ${pal.border}`,
            borderRadius: 99,
            padding: "1px 5px",
            flexShrink: 0,
          }}
        >
          {isRoot ? "★" : `F${node.depth}`}
        </span>
      )}
      {heatMode && heatTier && !isRoot && (
        <span
          style={{
            fontSize: 8,
            fontWeight: 800,
            color: pal.text,
            background: "rgba(0,0,0,0.4)",
            border: `1px solid ${pal.border}`,
            borderRadius: 99,
            padding: "1px 5px",
            flexShrink: 0,
          }}
        >
          {HEAT_PALETTE[heatTier].emoji}
        </span>
      )}
      {!heatMode && !isRoot && (node.volume_weight_pct ?? 0) > 0 && (
        <span
          style={{
            fontSize: 7,
            fontWeight: 700,
            color: "#fde68a",
            background: "rgba(245,158,11,0.2)",
            border: "1px solid rgba(245,158,11,0.4)",
            borderRadius: 99,
            padding: "1px 4px",
            flexShrink: 0,
          }}
        >
          {(node.volume_weight_pct ?? 0).toFixed(0)}%
        </span>
      )}
    </div>
  );
};

const LRNode: React.FC<{
  node: ReferralTreeNode;
  isRoot?: boolean;
  expanded: Set<number>;
  onToggle: (id: number) => void;
  onSelect: (n: ReferralTreeNode) => void;
  matchIds: Set<number>;
  visibleIds: Set<number> | null;
  heatMode: boolean;
  heatMap: Map<number, HeatTier>;
  registerRef: (id: number, el: HTMLDivElement | null) => void;
}> = ({
  node,
  isRoot = false,
  expanded,
  onToggle,
  onSelect,
  matchIds,
  visibleIds,
  heatMode,
  heatMap,
  registerRef,
}) => {
  if (!isRoot && visibleIds !== null && !visibleIds.has(node.id)) return null;
  const hasChildren = (node.children?.length ?? 0) > 0;
  const isExpanded = expanded.has(node.id);
  const pal = dp(isRoot ? 0 : node.depth);
  const heatTier = heatMap.get(node.id);
  const visibleChildren = hasChildren
    ? node.children.filter((c) => visibleIds === null || visibleIds.has(c.id))
    : [];
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <TreeCard
          node={node}
          isRoot={isRoot}
          highlighted={matchIds.has(node.id)}
          heatMode={heatMode}
          heatTier={heatTier}
          onSelect={onSelect}
          cardRef={(el) => registerRef(node.id, el)}
        />
        {visibleChildren.length > 0 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggle(node.id);
              }}
              style={{
                marginTop: 6,
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: `1.5px solid ${pal.border}`,
                background: "#0f172a",
                color: pal.text,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 700,
                boxShadow: `0 0 6px ${pal.glow}`,
                flexShrink: 0,
              }}
            >
              {isExpanded ? "−" : "+"}
            </button>
            {!isExpanded && (
              <div style={{ fontSize: 9, color: "#475569", marginTop: 2 }}>
                {visibleChildren.length}
              </div>
            )}
          </>
        )}
      </div>
      {visibleChildren.length > 0 && isExpanded && (
        <div
          style={{
            marginLeft: COL_GAP,
            display: "flex",
            flexDirection: "column",
            gap: ROW_GAP,
            paddingTop: 2,
          }}
        >
          {node.children.map((child) => (
            <LRNode
              key={child.id}
              node={child}
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
              matchIds={matchIds}
              visibleIds={visibleIds}
              heatMode={heatMode}
              heatMap={heatMap}
              registerRef={registerRef}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SVGLines: React.FC<{
  root: ReferralTreeNode | null;
  expanded: Set<number>;
  refs: Map<number, HTMLDivElement>;
  containerEl: HTMLDivElement | null;
  heatMode: boolean;
  heatMap: Map<number, HeatTier>;
}> = ({ root, expanded, refs, containerEl, heatMode, heatMap }) => {
  const [paths, setPaths] = useState<
    Array<{ d: string; color: string; key: string }>
  >([]);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    if (!root || !containerEl) return;
    const t = setTimeout(() => {
      const cr = containerEl.getBoundingClientRect();
      const result: typeof paths = [];
      let maxX = 0,
        maxY = 0;
      const visit = (node: ReferralTreeNode) => {
        if (!expanded.has(node.id)) return;
        const pEl = refs.get(node.id);
        if (!pEl) return;
        const pr = pEl.getBoundingClientRect();
        const px = pr.right - cr.left + containerEl.scrollLeft;
        const py = pr.top + pr.height / 2 - cr.top + containerEl.scrollTop;
        for (const child of node.children ?? []) {
          const cEl = refs.get(child.id);
          if (cEl) {
            const cr2 = cEl.getBoundingClientRect();
            const cx = cr2.left - cr.left + containerEl.scrollLeft;
            const cy =
              cr2.top + cr2.height / 2 - cr.top + containerEl.scrollTop;
            const mx = (cx - px) * 0.55;
            const color = heatMode
              ? HEAT_PALETTE[heatMap.get(child.id) ?? "dormant"].border + "88"
              : dp(child.depth ?? 1).border + "99";
            result.push({
              d: `M${px},${py} C${px + mx},${py} ${cx - mx},${cy} ${cx},${cy}`,
              color,
              key: `${node.id}-${child.id}`,
            });
            maxX = Math.max(maxX, cx);
            maxY = Math.max(maxY, cy);
          }
          visit(child);
        }
      };
      visit(root);
      setPaths(result);
      setSize({ w: maxX + 40, h: maxY + 40 });
    }, 40);
    return () => clearTimeout(t);
  }, [root, expanded, refs, containerEl, heatMode, heatMap]);
  if (paths.length === 0) return null;
  return (
    <svg
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: size.w,
        height: size.h,
        pointerEvents: "none",
        overflow: "visible",
      }}
    >
      {paths.map((p) => (
        <path
          key={p.key}
          d={p.d}
          stroke={p.color}
          strokeWidth={1.5}
          fill="none"
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
};

// ─── Filter Panel (mobile bottom-sheet) ──────────────────────────────────────

const FilterPanel: React.FC<{
  filters: TreeFilters;
  onChange: (f: TreeFilters) => void;
  maxDepthInTree: number;
  onClose: () => void;
}> = ({ filters, onChange, maxDepthInTree, onClose }) => {
  const f = filters;
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 29,
          background: "rgba(0,0,0,0.4)",
        }}
      />
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 30,
          background: "#0f172a",
          border: "1.5px solid rgba(255,255,255,0.1)",
          borderRadius: "14px 14px 0 0",
          boxShadow: "0 -8px 32px rgba(0,0,0,0.7)",
          padding: "16px 16px 32px",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <p style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9" }}>
            Filter Tree
          </p>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#64748b",
              cursor: "pointer",
              fontSize: 22,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label
              style={{
                fontSize: 11,
                color: "#94a3b8",
                fontWeight: 600,
                display: "block",
                marginBottom: 6,
              }}
            >
              Max Depth to Show
            </label>
            <select
              value={f.depthMax ?? "all"}
              onChange={(e) =>
                onChange({
                  ...f,
                  depthMax:
                    e.target.value === "all" ? null : Number(e.target.value),
                })
              }
              style={{
                width: "100%",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 8,
                color: "#f1f5f9",
                padding: "6px 10px",
                fontSize: 12,
              }}
            >
              <option value="all">All depths</option>
              {Array.from(
                { length: Math.min(maxDepthInTree, 15) },
                (_, i) => i + 1,
              ).map((d) => (
                <option key={d} value={d}>
                  Up to F{d}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              style={{
                fontSize: 11,
                color: "#94a3b8",
                fontWeight: 600,
                display: "block",
                marginBottom: 6,
              }}
            >
              Status
            </label>
            <div className="flex gap-2">
              {[
                { val: false, label: "All" },
                { val: true, label: "Active only" },
              ].map(({ val, label }) => (
                <button
                  key={label}
                  onClick={() => onChange({ ...f, activeOnly: val })}
                  style={{
                    flex: 1,
                    padding: "5px 8px",
                    borderRadius: 7,
                    border:
                      f.activeOnly === val
                        ? "1.5px solid #4ade80"
                        : "1px solid rgba(255,255,255,0.08)",
                    background:
                      f.activeOnly === val
                        ? "rgba(34,197,94,0.12)"
                        : "rgba(255,255,255,0.04)",
                    color: f.activeOnly === val ? "#4ade80" : "#94a3b8",
                    fontSize: 11,
                    cursor: "pointer",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label
              style={{
                fontSize: 11,
                color: "#94a3b8",
                fontWeight: 600,
                display: "block",
                marginBottom: 6,
              }}
            >
              Deposit Status
            </label>
            <div className="flex gap-2">
              {[
                { val: null, label: "All" },
                { val: true, label: "Deposited" },
                { val: false, label: "No deposit" },
              ].map(({ val, label }) => (
                <button
                  key={label}
                  onClick={() => onChange({ ...f, hasDeposit: val })}
                  style={{
                    flex: 1,
                    padding: "5px 4px",
                    borderRadius: 7,
                    border:
                      f.hasDeposit === val
                        ? "1.5px solid #60a5fa"
                        : "1px solid rgba(255,255,255,0.08)",
                    background:
                      f.hasDeposit === val
                        ? "rgba(59,130,246,0.12)"
                        : "rgba(255,255,255,0.04)",
                    color: f.hasDeposit === val ? "#93c5fd" : "#94a3b8",
                    fontSize: 10,
                    cursor: "pointer",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label
              style={{
                fontSize: 11,
                color: "#94a3b8",
                fontWeight: 600,
                display: "block",
                marginBottom: 6,
              }}
            >
              Min Volume Contribution (XRP)
            </label>
            <input
              type="number"
              min="0"
              step="10"
              value={f.minContrib || ""}
              onChange={(e) =>
                onChange({ ...f, minContrib: parseFloat(e.target.value) || 0 })
              }
              placeholder="0 = show all"
              style={{
                width: "100%",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 8,
                color: "#f1f5f9",
                padding: "6px 10px",
                fontSize: 12,
              }}
            />
          </div>
          <div>
            <label
              style={{
                fontSize: 11,
                color: "#94a3b8",
                fontWeight: 600,
                display: "block",
                marginBottom: 6,
              }}
            >
              Heat Tier
            </label>
            <div className="flex gap-2">
              {(["all", "top", "mid", "dormant"] as const).map((tier) => (
                <button
                  key={tier}
                  onClick={() => onChange({ ...f, heatTier: tier })}
                  style={{
                    flex: 1,
                    padding: "5px 4px",
                    borderRadius: 7,
                    border:
                      f.heatTier === tier
                        ? `1.5px solid ${tier === "all" ? "#818cf8" : HEAT_PALETTE[tier]?.border}`
                        : "1px solid rgba(255,255,255,0.08)",
                    background:
                      f.heatTier === tier
                        ? tier === "all"
                          ? "rgba(99,102,241,0.12)"
                          : HEAT_PALETTE[tier]?.bg
                        : "rgba(255,255,255,0.04)",
                    color:
                      f.heatTier === tier
                        ? tier === "all"
                          ? "#c7d2fe"
                          : HEAT_PALETTE[tier]?.text
                        : "#94a3b8",
                    fontSize: 10,
                    cursor: "pointer",
                  }}
                >
                  {tier === "all"
                    ? "All"
                    : HEAT_PALETTE[tier].emoji + " " + tier}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() =>
              onChange({
                depthMax: null,
                activeOnly: false,
                hasDeposit: null,
                minContrib: 0,
                heatTier: "all",
                heatMode: f.heatMode,
              })
            }
            style={{
              width: "100%",
              padding: "7px",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.08)",
              background: "rgba(255,255,255,0.04)",
              color: "#64748b",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Reset Filters
          </button>
        </div>
      </div>
    </>
  );
};

// ─── LR Tree ─────────────────────────────────────────────────────────────────

const LRTree: React.FC<{
  rootNode: ReferralTreeNode;
  expanded: Set<number>;
  onToggle: (id: number) => void;
  onSelect: (n: ReferralTreeNode) => void;
  heatMode: boolean;
  heatMap: Map<number, HeatTier>;
  heatTopThreshold: number;
  heatMidThreshold: number;
  filters: TreeFilters;
  onFiltersChange: (f: TreeFilters) => void;
  visibleIds: Set<number> | null;
}> = ({
  rootNode,
  expanded,
  onToggle,
  onSelect,
  heatMode,
  heatMap,
  heatTopThreshold,
  heatMidThreshold,
  filters,
  onFiltersChange,
  visibleIds,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const refsMap = useRef<Map<number, HTMLDivElement>>(new Map());
  const [search, setSearch] = useState("");
  const [tick, setTick] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const registerRef = useCallback((id: number, el: HTMLDivElement | null) => {
    if (el) refsMap.current.set(id, el);
    else refsMap.current.delete(id);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setTick((n) => n + 1), 60);
    return () => clearTimeout(t);
  }, [expanded, visibleIds, heatMode]);
  const matchIds = useMemo(() => {
    if (!search.trim()) return new Set<number>();
    const q = search.toLowerCase();
    const ids = new Set<number>();
    const walk = (n: ReferralTreeNode) => {
      if (
        n.username?.toLowerCase().includes(q) ||
        n.name?.toLowerCase().includes(q)
      )
        ids.add(n.id);
      n.children?.forEach(walk);
    };
    walk(rootNode);
    return ids;
  }, [search, rootNode]);
  const activeFilterCount = [
    filters.depthMax !== null,
    filters.activeOnly,
    filters.hasDeposit !== null,
    filters.minContrib > 0,
    filters.heatTier !== "all",
  ].filter(Boolean).length;

  const topPct = Math.round(heatTopThreshold * 100);
  const midPct = Math.round((heatMidThreshold - heatTopThreshold) * 100);
  const dormPct = Math.round((1 - heatMidThreshold) * 100);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          flexShrink: 0,
          overflowX: "auto",
          WebkitOverflowScrolling: "touch" as any,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 14px",
            minWidth: "max-content",
          }}
        >
          <div style={{ position: "relative", width: 180, flexShrink: 0 }}>
            <MagnifyingGlassIcon
              style={{
                position: "absolute",
                left: 9,
                top: "50%",
                transform: "translateY(-50%)",
                width: 13,
                height: 13,
                color: "#64748b",
                pointerEvents: "none",
              }}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              style={{
                width: "100%",
                paddingLeft: 28,
                paddingRight: search ? 26 : 10,
                paddingTop: 5,
                paddingBottom: 5,
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.09)",
                borderRadius: 7,
                color: "#f1f5f9",
                fontSize: 12,
                outline: "none",
              }}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                style={{
                  position: "absolute",
                  right: 7,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: "#64748b",
                  cursor: "pointer",
                  fontSize: 14,
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                ×
              </button>
            )}
          </div>
          {search && (
            <span
              style={{
                fontSize: 11,
                color: matchIds.size > 0 ? "#818cf8" : "#ef4444",
                flexShrink: 0,
              }}
            >
              {matchIds.size > 0 ? `${matchIds.size}` : "0"}
            </span>
          )}
          <button
            onClick={() => onFiltersChange({ ...filters, heatMode: !heatMode })}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              padding: "5px 10px",
              borderRadius: 7,
              border: `1.5px solid ${heatMode ? "#f97316" : "rgba(255,255,255,0.09)"}`,
              background: heatMode
                ? "rgba(249,115,22,0.15)"
                : "rgba(255,255,255,0.05)",
              color: heatMode ? "#fed7aa" : "#94a3b8",
              fontSize: 11,
              cursor: "pointer",
              flexShrink: 0,
              fontWeight: heatMode ? 700 : 400,
              whiteSpace: "nowrap",
            }}
          >
            <FireIcon style={{ width: 13, height: 13 }} />
            {heatMode ? "Heat ON" : "Heat"}
          </button>
          <button
            onClick={() => setShowFilters((v) => !v)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              padding: "5px 10px",
              borderRadius: 7,
              border: `1.5px solid ${activeFilterCount > 0 ? "#818cf8" : "rgba(255,255,255,0.09)"}`,
              background:
                activeFilterCount > 0
                  ? "rgba(99,102,241,0.15)"
                  : "rgba(255,255,255,0.05)",
              color: activeFilterCount > 0 ? "#c7d2fe" : "#94a3b8",
              fontSize: 11,
              cursor: "pointer",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            <AdjustmentsHorizontalIcon style={{ width: 13, height: 13 }} />
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
          <span style={{ fontSize: 10, color: "#1e293b", flexShrink: 0 }}>
            ↔↕ scroll
          </span>
        </div>
      </div>
      {showFilters && (
        <FilterPanel
          filters={filters}
          onChange={onFiltersChange}
          maxDepthInTree={15}
          onClose={() => setShowFilters(false)}
        />
      )}
      {heatMode && (
        <div
          style={{
            padding: "6px 14px",
            background: "rgba(249,115,22,0.06)",
            borderBottom: "1px solid rgba(249,115,22,0.15)",
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 12px",
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 10, color: "#94a3b8", marginRight: 4 }}>
            Heat Map:
          </span>
          {(["top", "mid", "dormant"] as const).map((tier) => (
            <span
              key={tier}
              style={{
                fontSize: 10,
                color: HEAT_PALETTE[tier].text,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: HEAT_PALETTE[tier].border,
                  display: "inline-block",
                }}
              />
              {HEAT_PALETTE[tier].label}
              <span style={{ color: "#64748b" }}>
                (
                {tier === "top"
                  ? `top ${topPct}%`
                  : tier === "mid"
                    ? `next ${midPct}%`
                    : `bottom ${dormPct}%`}
                )
              </span>
            </span>
          ))}
        </div>
      )}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          overflow: "auto",
          position: "relative",
          WebkitOverflowScrolling: "touch" as any,
        }}
      >
        <div
          style={{
            padding: "24px 32px 56px",
            minWidth: "max-content",
            position: "relative",
          }}
        >
          <SVGLines
            key={tick}
            root={rootNode}
            expanded={expanded}
            refs={refsMap.current}
            containerEl={containerRef.current}
            heatMode={heatMode}
            heatMap={heatMap}
          />
          <div style={{ position: "relative", zIndex: 1 }}>
            <LRNode
              node={rootNode}
              isRoot
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
              matchIds={matchIds}
              visibleIds={visibleIds}
              heatMode={heatMode}
              heatMap={heatMap}
              registerRef={registerRef}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Volume Breakdown Table ───────────────────────────────────────────────────

const VolumeBreakdownTable: React.FC<{
  breakdown: Record<string, VolumeBreakdownRow>;
  totalVolume: number;
  floorDepth?: number;
}> = ({ breakdown, totalVolume, floorDepth }) => {
  const rows = Object.entries(breakdown)
    .map(([key, row]) => ({
      key,
      depth: key === "∞" ? Infinity : Number(key),
      ...row,
    }))
    .filter((r) => r.members > 0 || r.raw_deposits > 0)
    .sort((a, b) => a.depth - b.depth);
  if (rows.length === 0)
    return (
      <p className="text-gray-500 text-sm text-center py-6">
        No data yet — invite people to build your team!
      </p>
    );
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.02)",
        borderRadius: 10,
        border: "1px solid rgba(255,255,255,0.07)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "10px 14px",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <p style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8" }}>
          Volume Breakdown by Depth
        </p>
        <p style={{ fontSize: 11, color: "#fbbf24", fontWeight: 700 }}>
          Total: {formatXRP(totalVolume)} XRP (weighted)
        </p>
      </div>
      <div style={sw}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 11,
            minWidth: 500,
          }}
        >
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
              {[
                "Depth",
                "Members",
                "Raw Deposits",
                "Weight",
                "Weighted Vol",
                "% of Total",
              ].map((h) => (
                <th
                  key={h}
                  style={{
                    padding: "7px 10px",
                    textAlign: "left",
                    color: "#64748b",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isFloor = row.key === "∞";
              const pal = isFloor ? dp(10) : dp(Number(row.key));
              const pct =
                totalVolume > 0 ? (row.weighted_volume / totalVolume) * 100 : 0;
              return (
                <tr
                  key={row.key}
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                >
                  <td style={{ padding: "7px 10px" }}>
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: isFloor ? 6 : "50%",
                        border: `2px solid ${pal.border}`,
                        background: pal.bg,
                        color: pal.text,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 9,
                        fontWeight: 800,
                      }}
                    >
                      {isFloor
                        ? (row.depth_label ?? `F${floorDepth}+`)
                        : `F${row.key}`}
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "7px 10px",
                      color: "#e2e8f0",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.members}
                  </td>
                  <td
                    style={{
                      padding: "7px 10px",
                      color: "#93c5fd",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatXRP(row.raw_deposits)} XRP
                  </td>
                  <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>
                    <span
                      style={{
                        background: "rgba(245,158,11,0.12)",
                        border: "1px solid rgba(251,191,36,0.3)",
                        borderRadius: 6,
                        padding: "2px 7px",
                        color: "#fde68a",
                        fontWeight: 700,
                      }}
                    >
                      {(row.weight * 100).toFixed(0)}%{isFloor ? " ∞" : ""}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: "7px 10px",
                      color: "#fbbf24",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatXRP(row.weighted_volume)} XRP
                  </td>
                  <td style={{ padding: "7px 10px" }}>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <div
                        style={{
                          flex: 1,
                          height: 4,
                          background: "rgba(255,255,255,0.06)",
                          borderRadius: 99,
                          minWidth: 40,
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${Math.min(pct, 100)}%`,
                            background: pal.border,
                            borderRadius: 99,
                          }}
                        />
                      </div>
                      <span
                        style={{
                          color: "#94a3b8",
                          minWidth: 36,
                          textAlign: "right",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {pct.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Node Detail Modal Content ────────────────────────────────────────────────

const NodeDetailContent: React.FC<{
  node: ReferralTreeNode;
  myUsername: string;
  heatTier?: HeatTier;
  onClose: () => void;
}> = ({ node, myUsername, heatTier, onClose }) => {
  const isMe = node.id === -1;
  const pal = dp(isMe ? 0 : node.depth);
  const pct =
    node.commission_pct ??
    (node.commission_rate ? node.commission_rate * 100 : null);
  const hasDeposit = !isMe && node.total_deposits > 0;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 p-4 bg-gray-800/50 rounded-lg">
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            border: `2.5px solid ${heatTier ? HEAT_PALETTE[heatTier].border : pal.border}`,
            background: heatTier ? HEAT_PALETTE[heatTier].bg : pal.bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 21,
            fontWeight: 800,
            color: heatTier ? HEAT_PALETTE[heatTier].text : pal.text,
          }}
        >
          {node.username?.charAt(0).toUpperCase()}
        </div>
        <div>
          <p className="text-base font-semibold text-white">{node.name}</p>
          <p className="text-sm text-gray-400">@{node.username}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {!isMe && (
              <span
                style={{
                  background: pal.bg,
                  border: `1px solid ${pal.border}`,
                  color: pal.text,
                }}
                className="px-2 py-0.5 text-xs rounded-full font-bold"
              >
                F{node.depth}
              </span>
            )}
            {heatTier && !isMe && (
              <span
                style={{
                  background: HEAT_PALETTE[heatTier].bg,
                  border: `1px solid ${HEAT_PALETTE[heatTier].border}`,
                  color: HEAT_PALETTE[heatTier].text,
                }}
                className="px-2 py-0.5 text-xs rounded-full font-bold"
              >
                {HEAT_PALETTE[heatTier].label}
              </span>
            )}
            <span
              className={`px-2 py-0.5 text-xs rounded-full ${node.is_active ? "bg-green-900/50 text-green-300 border border-green-700" : "bg-gray-700 text-gray-300"}`}
            >
              {node.is_active ? "Active" : "Inactive"}
            </span>
          </div>
        </div>
      </div>
      {!isMe && (node.volume_weight_pct ?? 0) > 0 && (
        <div
          style={{
            background: "rgba(245,158,11,0.07)",
            border: "1.5px solid rgba(251,191,36,0.3)",
            borderRadius: 12,
            padding: "12px 16px",
          }}
        >
          <p
            style={{
              fontSize: 11,
              color: "#fde68a",
              fontWeight: 600,
              marginBottom: 8,
            }}
          >
            📊 Volume Contribution to @{myUsername}
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <div className="text-center">
              <p style={{ fontSize: 9, color: "#64748b", marginBottom: 2 }}>
                Raw Deposits
              </p>
              <p style={{ fontSize: 14, fontWeight: 700, color: "#93c5fd" }}>
                {formatXRP(node.total_deposits)} XRP
              </p>
            </div>
            <span style={{ color: "#475569", fontSize: 16 }}>×</span>
            <div className="text-center">
              <p style={{ fontSize: 9, color: "#64748b", marginBottom: 2 }}>
                F{node.depth} Weight
              </p>
              <span
                style={{
                  background: "rgba(245,158,11,0.15)",
                  border: "1px solid rgba(251,191,36,0.4)",
                  borderRadius: 6,
                  padding: "3px 10px",
                  color: "#fde68a",
                  fontSize: 13,
                  fontWeight: 800,
                }}
              >
                {(node.volume_weight_pct ?? 0).toFixed(0)}%
              </span>
            </div>
            <span style={{ color: "#475569", fontSize: 16 }}>=</span>
            <div className="text-center">
              <p style={{ fontSize: 9, color: "#64748b", marginBottom: 2 }}>
                Weighted Contribution
              </p>
              <p style={{ fontSize: 16, fontWeight: 800, color: "#fbbf24" }}>
                {formatXRP(node.weighted_contribution ?? 0)} XRP
              </p>
            </div>
          </div>
        </div>
      )}
      {!isMe && pct !== null && pct > 0 && (
        <div
          style={{
            background: pal.bg,
            border: `1.5px solid ${pal.border}`,
            color: pal.text,
          }}
          className="rounded-lg p-3 text-center"
        >
          <p className="text-xs mb-0.5 opacity-70">
            Commission Rate (on 1st deposit)
          </p>
          <p className="text-xl font-bold">{pct.toFixed(1)}%</p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-800/30 rounded-lg p-3">
          <p className="text-xs text-gray-400 mb-1">XRP Balance</p>
          <p className="text-base font-semibold text-blue-400">
            {formatXRP(node.xrp_balance)} XRP
          </p>
        </div>
        <div className="bg-gray-800/30 rounded-lg p-3">
          <p className="text-xs text-gray-400 mb-1">Their Team Vol</p>
          <p className="text-base font-semibold text-purple-400">
            {formatXRP(node.team_volume)} XRP
          </p>
        </div>
        {!isMe && (
          <div
            style={{
              background: hasDeposit
                ? "rgba(34,197,94,0.10)"
                : "rgba(255,255,255,0.03)",
              border: `1.5px solid ${hasDeposit ? "rgba(74,222,128,0.35)" : "rgba(255,255,255,0.07)"}`,
            }}
            className="rounded-lg p-3"
          >
            <p className="text-xs text-gray-400 mb-1">Total Deposits</p>
            <p
              className={`text-base font-semibold ${hasDeposit ? "text-green-400" : "text-gray-600"}`}
            >
              {hasDeposit
                ? `${formatXRP(node.total_deposits)} XRP`
                : "No deposit yet"}
            </p>
          </div>
        )}
        <div className="bg-gray-800/30 rounded-lg p-3">
          <p className="text-xs text-gray-400 mb-1">Direct Referrals</p>
          <p className="text-base font-semibold text-white">
            {node.children?.length || 0}
          </p>
        </div>
      </div>
      {!isMe && hasDeposit && pct !== null && pct > 0 && (
        <div className="bg-yellow-900/15 border border-yellow-800/30 rounded-lg p-3 flex items-center justify-between">
          <div>
            <p className="text-xs text-yellow-400/70 mb-0.5">
              Commission from their 1st deposit
            </p>
            <p className="text-sm font-bold text-yellow-400">
              {formatXRP((node.first_deposit * pct) / 100)} XRP
            </p>
          </div>
          <div className="text-yellow-500 text-lg">💰</div>
        </div>
      )}
      <Button variant="tertiary" onClick={onClose} fullWidth>
        Close
      </Button>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const Referral: React.FC = () => {
  const reduxBalance = useSelector((s: RootState) => s.wallet?.balance);
  const reduxUser = useSelector((s: RootState) => s.auth?.user);

  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [referralTree, setReferralTree] = useState<ReferralTreeNode[]>([]);
  const [breakdown, setBreakdown] = useState<
    Record<string, VolumeBreakdownRow>
  >({});
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingTree, setLoadingTree] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<"overview" | "tree" | "earnings">(
    "overview",
  );
  const [treeSubTab, setTreeSubTab] = useState<"tree" | "breakdown">("tree");
  const [expanded, setExpanded] = useState<Set<number>>(new Set([-1]));
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [selectedNode, setSelectedNode] = useState<ReferralTreeNode | null>(
    null,
  );
  const [showNodeDetails, setShowNodeDetails] = useState(false);
  const [filters, setFilters] = useState<TreeFilters>({
    depthMax: null,
    activeOnly: false,
    hasDeposit: null,
    minContrib: 0,
    heatTier: "all",
    heatMode: false,
  });

  useEffect(() => {
    loadStats();
  }, []); // eslint-disable-line
  useEffect(() => {
    loadTree();
  }, []); // eslint-disable-line

  const loadStats = async () => {
    try {
      setLoadingStats(true);
      setError(null);
      const sr = await referralService.getStats();
      setStats(sr.data);
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed to load referral data");
    } finally {
      setLoadingStats(false);
    }
  };

  const loadTree = async () => {
    try {
      setLoadingTree(true);
      const r = await referralService.getReferralTree();
      const raw: ReferralTreeNode[] = r.data.tree || [];
      const rawBreakdown = r.data.breakdown || {};
      const addDepth = (ns: ReferralTreeNode[], d = 1): ReferralTreeNode[] =>
        ns.map((n) => ({
          ...n,
          depth: n.depth ?? d,
          children: addDepth(n.children || [], d + 1),
        }));
      const tree = addDepth(raw);
      setReferralTree(tree);
      setBreakdown(rawBreakdown);
      setExpanded(collectAllIds(tree, new Set([-1])));
    } catch (e: any) {
      console.error("Tree load error:", e);
      setReferralTree([]);
    } finally {
      setLoadingTree(false);
    }
  };

  const copyToClipboard = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setSuccess("Referral link copied!");
    setTimeout(() => {
      setCopied(false);
      setSuccess(null);
    }, 2000);
  };

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  const expandAll = () =>
    setExpanded(collectAllIds(referralTree, new Set([-1])));
  const collapseAll = () => setExpanded(new Set([-1]));

  const getMyXrpBalance = () => {
    if (reduxBalance?.xrp_balance !== undefined)
      return parseFloat(String(reduxBalance.xrp_balance)) || 0;
    if (reduxUser?.xrp_balance)
      return parseFloat(String(reduxUser.xrp_balance)) || 0;
    return 0;
  };

  // Heat map thresholds — read from admin settings, fall back to 20/70
  const heatTopThreshold = stats?.referral_settings?.heat_top_threshold ?? 0.2;
  const heatMidThreshold = stats?.referral_settings?.heat_mid_threshold ?? 0.7;

  const allFlatNodes = useMemo(() => flattenTree(referralTree), [referralTree]);
  const heatMap = useMemo(
    () => buildHeatMap(allFlatNodes, heatTopThreshold, heatMidThreshold),
    [allFlatNodes, heatTopThreshold, heatMidThreshold],
  );

  const visibleIds: Set<number> | null = useMemo(() => {
    const hasFilter =
      filters.depthMax !== null ||
      filters.activeOnly ||
      filters.hasDeposit !== null ||
      filters.minContrib > 0 ||
      filters.heatTier !== "all";
    if (!hasFilter) return null;
    const ids = new Set<number>();
    const walk = (n: ReferralTreeNode) => {
      if (n.id === -1) {
        ids.add(n.id);
        n.children.forEach(walk);
        return;
      }
      const depthOk = filters.depthMax === null || n.depth <= filters.depthMax;
      const activeOk = !filters.activeOnly || n.is_active;
      const depositOk =
        filters.hasDeposit === null ||
        (filters.hasDeposit ? n.total_deposits > 0 : n.total_deposits === 0);
      const contribOk = (n.weighted_contribution ?? 0) >= filters.minContrib;
      const tierOk =
        filters.heatTier === "all" || heatMap.get(n.id) === filters.heatTier;
      if (depthOk && activeOk && depositOk && contribOk && tierOk)
        ids.add(n.id);
      n.children.forEach(walk);
    };
    walk({
      id: -1,
      username: "",
      name: "",
      depth: 0,
      first_deposit: 0,
      total_deposits: 0,
      team_volume: 0,
      xrp_balance: 0,
      joined_at: "",
      is_active: true,
      children: referralTree,
    });
    return ids;
  }, [filters, referralTree, heatMap]);

  const rootNode: ReferralTreeNode = useMemo(
    () => ({
      id: -1,
      username: reduxUser?.username || stats?.username || "You",
      name: reduxUser?.first_name || stats?.name || "You",
      depth: 0,
      commission_rate: 0,
      commission_pct: 0,
      volume_weight: 0,
      volume_weight_pct: 0,
      first_deposit: 0,
      total_deposits: 0,
      weighted_contribution: 0,
      team_volume: stats?.team_volume || 0,
      xrp_balance: getMyXrpBalance(),
      joined_at: new Date().toISOString(),
      is_active: true,
      children: referralTree,
    }),
    [referralTree, stats, reduxUser, reduxBalance],
  ); // eslint-disable-line

  const userLevel = stats?.user_level ?? 0;
  const isUnranked = userLevel === 0;
  const depthRates = stats ? getDepthRates(stats) : {};
  const depthWeights = stats ? getDepthWeights(stats) : {};
  const floorWeight = stats ? getFloorWeight(stats) : 0;
  const maxDepths =
    stats?.referral_settings?.commission_levels ??
    stats?.referral_settings?.levels ??
    5;
  const maxVolDepths =
    stats?.referral_settings?.volume_depth_levels ??
    stats?.referral_settings?.levels ??
    10;
  const totalLevels = stats?.referral_settings?.levels ?? 5;
  const myUsername = reduxUser?.username || stats?.username || "You";
  const visibleCount = visibleIds ? visibleIds.size - 1 : allFlatNodes.length;

  // ── Level progress (new reset logic from backend) ────────────────────────
  const levelProgress = stats?.level_progress;
  const progressVolume = levelProgress?.progress_volume ?? 0; // earned since this tier started
  const neededVolume = levelProgress?.needed_volume ?? 0; // gap to next tier
  const progressPct = levelProgress?.progress_pct ?? 0; // 0–100, resets per tier
  const atMax = levelProgress?.at_max ?? false;
  const currentThreshold = levelProgress?.current_threshold ?? 0;
  const nextThreshold = levelProgress?.next_threshold ?? null;
  const showProgressBar = !isUnranked && !atMax && nextThreshold !== null;

  // ── Total Sales ────────────────────────────────────────────────────────────
  const totalSales = stats?.total_sales ?? 0;

  // heat label helpers
  const topPct = Math.round(heatTopThreshold * 100);
  const midPct = Math.round((heatMidThreshold - heatTopThreshold) * 100);
  const dormPct = Math.round((1 - heatMidThreshold) * 100);

  return (
    <div className={globalStyle.container}>
      <Header />
      <main className="page-content pb-24">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className={globalStyle.h3}>Affiliate Program</h1>
            <p className={`${globalStyle.body2} text-gray-400`}>
              Build your team and earn weighted rewards
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => setShowHowItWorks(true)}
              leftIcon={<InformationCircleIcon className="w-4 h-4" />}
            >
              How It Works
            </Button>
            {!loadingStats && stats && (
              <div
                className={`px-4 py-2 rounded-full border ${lvlBadgeClass(userLevel)} flex items-center gap-2`}
              >
                {isUnranked ? (
                  <LockClosedIcon className="w-4 h-4" />
                ) : (
                  <StarIcon className="w-4 h-4" />
                )}
                <span className="font-medium whitespace-nowrap">
                  {isUnranked
                    ? "Unranked"
                    : stats.level_name || `Level ${userLevel}`}
                </span>
              </div>
            )}
          </div>
        </div>

        {error && (
          <Alert
            type="error"
            message={error}
            onClose={() => setError(null)}
            className="mb-6"
          />
        )}
        {success && (
          <Alert
            type="success"
            message={success}
            onClose={() => setSuccess(null)}
            className="mb-6"
          />
        )}

        {/* Referral Link */}
        <Card className="mb-6 bg-gradient-to-r from-blue-900 to-purple-900">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <h2 className={`${globalStyle.h4} text-white`}>
                Your Referral Link
              </h2>
              <p className="text-blue-200 mt-1">
                Share this link to grow your team
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
              <div className="flex-1 min-w-0 bg-black/30 rounded-lg p-3 overflow-hidden border border-white/10">
                <code className="block text-sm text-white truncate font-mono">
                  {stats?.referral_link || "Loading..."}
                </code>
              </div>
              <Button
                onClick={() => copyToClipboard(stats?.referral_link || "")}
                disabled={!stats?.referral_link}
                leftIcon={
                  copied ? (
                    <CheckCircleIcon className="w-4 h-4" />
                  ) : (
                    <DocumentDuplicateIcon className="w-4 h-4" />
                  )
                }
                className="whitespace-nowrap shrink-0"
              >
                {copied ? "Copied!" : "Copy"}
              </Button>
            </div>
          </div>
        </Card>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex border-b border-gray-800">
            {[
              { id: "overview", label: "Overview", icon: ChartBarIcon },
              { id: "tree", label: "Team Tree", icon: UserGroupIcon },
              { id: "earnings", label: "Earnings", icon: CurrencyDollarIcon },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                className={`flex-1 sm:flex-none px-4 py-3 font-medium ${globalStyle.transition} flex items-center justify-center gap-2 ${activeTab === id ? "text-primary border-b-2 border-primary" : "text-gray-400 hover:text-gray-300"}`}
                onClick={() => setActiveTab(id as any)}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{label}</span>
                <span className="sm:hidden text-xs">{label.split(" ")[0]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── OVERVIEW TAB ── */}
        {activeTab === "overview" && (
          <>
            {loadingStats ? (
              <LoadingScreen message="Loading overview..." />
            ) : (
              stats && (
                <>
                  {/* 4 main stat cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    {[
                      {
                        icon: UsersIcon,
                        bg: "blue",
                        label: "Total Referrals",
                        value: stats.total_referrals || 0,
                        sub: "Direct referrals",
                      },
                      {
                        icon: CheckCircleIcon,
                        bg: "green",
                        label: "Active Referrals",
                        value: stats.active_referrals || 0,
                        sub: "With deposits",
                      },
                      {
                        icon: CircleStackIcon,
                        bg: "purple",
                        label: "Weighted Volume",
                        value: `${formatXRP(stats.team_volume || 0)} XRP`,
                        sub: "Weighted across all depths",
                      },
                      {
                        icon: BanknotesIcon,
                        bg: "yellow",
                        label: "Total Earnings",
                        value: `${formatXRP(stats.total_earnings || 0)} XRP`,
                        sub: "Commissions + bonuses",
                      },
                    ].map(({ icon: Icon, bg, label, value, sub }) => (
                      <Card
                        key={label}
                        hover
                        padding="lg"
                        className="text-center"
                      >
                        <div
                          className={`w-12 h-12 rounded-full bg-${bg}-900 flex items-center justify-center mx-auto mb-3`}
                        >
                          <Icon className={`w-6 h-6 text-${bg}-300`} />
                        </div>
                        <p className="text-xs text-gray-400 mb-1">{label}</p>
                        <h3 className="text-xl font-bold text-white">
                          {value}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">{sub}</p>
                      </Card>
                    ))}
                  </div>

                  {/* TOTAL SALES — full-width card */}
                  <Card className="mb-6" padding="lg">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-orange-900 flex items-center justify-center flex-shrink-0">
                          <ArrowTrendingUpIcon className="w-6 h-6 text-orange-300" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-1">
                            Total Sales
                          </p>
                          <h3 className="text-2xl font-bold text-orange-400">
                            {formatXRP(totalSales)} XRP
                          </h3>
                          <p className="text-xs text-gray-500 mt-1">
                            Raw deposits from all {allFlatNodes.length} downline
                            members — no depth weighting
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500 mb-1">
                          vs Weighted Volume
                        </p>
                        <p className="text-lg font-semibold text-purple-400">
                          {formatXRP(stats.team_volume || 0)} XRP
                        </p>
                        {totalSales > 0 && (
                          <p className="text-xs text-gray-600 mt-0.5">
                            {(
                              ((stats.team_volume || 0) / totalSales) *
                              100
                            ).toFixed(1)}
                            % weight efficiency
                          </p>
                        )}
                      </div>
                    </div>
                  </Card>

                  {/* Tier Progress — RESET logic: progress_volume and needed_volume span only the current tier band */}
                  {showProgressBar && (
                    <Card className="mb-6" padding="lg">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="text-sm font-semibold text-white">
                            Tier Progress
                          </h3>
                          <p className="text-xs text-gray-400 mt-0.5">
                            Progress resets to 0 XRP at each tier boundary
                          </p>
                        </div>
                        <div
                          className={`px-3 py-1 rounded-full border text-xs font-bold ${lvlBadgeClass(userLevel)}`}
                        >
                          Level {userLevel} → {userLevel + 1}
                        </div>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2.5 mb-2">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-purple-500 h-2.5 rounded-full transition-all duration-700"
                          style={{ width: `${Math.min(progressPct, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>
                          {formatXRP(progressVolume)} XRP since Level{" "}
                          {userLevel}
                        </span>
                        <span>/ {formatXRP(neededVolume)} XRP needed</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1 text-right">
                        Level {userLevel + 1} requires{" "}
                        {formatXRP(nextThreshold!)} XRP total weighted volume
                      </p>
                    </Card>
                  )}
                  {atMax && (
                    <Card
                      className="mb-6 bg-gradient-to-r from-yellow-900/30 to-purple-900/30 border border-yellow-700/30"
                      padding="lg"
                    >
                      <div className="flex items-center gap-3">
                        <TrophyIcon className="w-8 h-8 text-yellow-400" />
                        <div>
                          <p className="font-bold text-yellow-300">
                            Maximum Tier Reached!
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            You've achieved the highest tier in the program.
                          </p>
                        </div>
                      </div>
                    </Card>
                  )}

                  {/* Weighted volume explainer */}
                  <div
                    style={{
                      background: "rgba(245,158,11,0.06)",
                      border: "1px solid rgba(251,191,36,0.2)",
                      borderRadius: 12,
                      padding: "12px 16px",
                      marginBottom: 16,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                    }}
                  >
                    <InformationCircleIcon
                      style={{
                        width: 18,
                        height: 18,
                        color: "#fbbf24",
                        flexShrink: 0,
                        marginTop: 1,
                      }}
                    />
                    <div>
                      <p
                        style={{
                          fontSize: 12,
                          color: "#fde68a",
                          fontWeight: 600,
                          marginBottom: 4,
                        }}
                      >
                        How Weighted Team Volume Works
                      </p>
                      <p
                        style={{
                          fontSize: 11,
                          color: "#94a3b8",
                          lineHeight: 1.7,
                        }}
                      >
                        Your team volume is a{" "}
                        <strong className="text-white">weighted sum</strong>{" "}
                        across unlimited depths. F1 ={" "}
                        <strong className="text-yellow-400">100%</strong>, F2 ={" "}
                        <strong className="text-yellow-400">
                          {((depthWeights[2] ?? 0) * 100).toFixed(0)}%
                        </strong>
                        , F3 ={" "}
                        <strong className="text-yellow-400">
                          {((depthWeights[3] ?? 0) * 100).toFixed(0)}%
                        </strong>
                        … Depths beyond F{maxVolDepths} each count at{" "}
                        <strong className="text-yellow-400">
                          {(floorWeight * 100).toFixed(0)}%
                        </strong>{" "}
                        (floor rate). Total Sales shows the raw unweighted sum.
                      </p>
                    </div>
                  </div>

                  {/* Commission + volume table */}
                  {stats.referral_settings && (
                    <Card padding="lg" className="mb-6">
                      <h3 className={globalStyle.h5}>
                        Commission & Volume Structure
                      </h3>
                      <div style={sw} className="mt-4">
                        <table className="w-full" style={{ minWidth: 460 }}>
                          <thead>
                            <tr className="border-b border-gray-800">
                              {[
                                "Depth",
                                "Who",
                                "Commission",
                                "Volume Weight",
                                "On 100 XRP deposit",
                              ].map((h) => (
                                <th
                                  key={h}
                                  className="py-3 px-3 text-left text-xs font-medium text-gray-400"
                                  style={nw}
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {Array.from(
                              { length: Math.max(maxDepths, maxVolDepths) },
                              (_, i) => i + 1,
                            ).map((depth) => {
                              const rate = depthRates[depth] ?? 0;
                              const weight = weightForDepth(
                                depth,
                                depthWeights,
                                floorWeight,
                              );
                              const pal = dp(depth);
                              const inComm = depth <= maxDepths;
                              const isFloor = depth > maxVolDepths;
                              return (
                                <tr
                                  key={depth}
                                  className="border-b border-gray-800 hover:bg-gray-800/30"
                                >
                                  <td className="py-3 px-3">
                                    <div
                                      style={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: "50%",
                                        border: `2px solid ${pal.border}`,
                                        background: pal.bg,
                                        color: pal.text,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: 10,
                                        fontWeight: 800,
                                      }}
                                    >
                                      F{depth}
                                    </div>
                                  </td>
                                  <td
                                    className="py-3 px-3 text-white text-sm"
                                    style={nw}
                                  >
                                    {depth === 1 ? "Direct" : `Depth-${depth}`}
                                  </td>
                                  <td className="py-3 px-3" style={nw}>
                                    {inComm ? (
                                      <span className="text-primary font-semibold text-sm">
                                        {(rate * 100).toFixed(1)}%
                                      </span>
                                    ) : (
                                      <span className="text-gray-700 text-xs">
                                        —
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-3 px-3" style={nw}>
                                    <div className="flex items-center gap-1.5">
                                      <span
                                        style={{
                                          background: "rgba(245,158,11,0.12)",
                                          border:
                                            "1px solid rgba(251,191,36,0.3)",
                                          borderRadius: 6,
                                          padding: "2px 8px",
                                          color: "#fde68a",
                                          fontSize: 11,
                                          fontWeight: 700,
                                        }}
                                      >
                                        {(weight * 100).toFixed(0)}%
                                      </span>
                                      {isFloor && (
                                        <span
                                          style={{
                                            fontSize: 9,
                                            color: "#64748b",
                                          }}
                                        >
                                          ∞ floor
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td
                                    className="py-3 px-3 text-xs space-x-2"
                                    style={nw}
                                  >
                                    {inComm && (
                                      <span className="text-blue-400">
                                        +{(100 * rate).toFixed(2)} comm.
                                      </span>
                                    )}
                                    <span className="text-yellow-400">
                                      {(100 * weight).toFixed(0)} vol.
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Tier table — uses Number(totalLevels), no hardcoded cap */}
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-300 mb-1 flex items-center gap-2">
                          <StarIcon className="w-4 h-4 text-yellow-500" /> Tier
                          Level-Up Bonuses
                        </h4>
                        <p className="text-xs text-gray-500 mb-3">
                          Based on{" "}
                          <span className="text-yellow-400">
                            weighted team volume
                          </span>
                          . Progress resets to 0 XRP at each tier. {totalLevels}{" "}
                          tiers configured.
                        </p>
                        <div style={sw}>
                          <table className="w-full" style={{ minWidth: 380 }}>
                            <thead>
                              <tr className="border-b border-gray-800">
                                {[
                                  "Level",
                                  "Name",
                                  "Min Weighted Volume",
                                  "One-Time Bonus",
                                ].map((h) => (
                                  <th
                                    key={h}
                                    className="py-3 px-3 text-left text-xs font-medium text-gray-400"
                                    style={nw}
                                  >
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {Array.from(
                                { length: totalLevels },
                                (_, i) => i + 1,
                              ).map((level) => {
                                const isCurrent =
                                  !isUnranked && level === userLevel;
                                const isNext = isUnranked
                                  ? level === 1
                                  : level === userLevel + 1;
                                const isLocked =
                                  isUnranked || level > userLevel;
                                const req = stats.referral_settings
                                  ?.level_requirements?.[level] || {
                                  min_volume: 0,
                                };
                                const bonus =
                                  stats.referral_settings?.level_bonuses?.[
                                    level
                                  ] || 0;
                                const name =
                                  stats.referral_settings?.level_names?.[
                                    level
                                  ] || `Level ${level}`;
                                return (
                                  <tr
                                    key={level}
                                    className={`border-b border-gray-800 ${isCurrent ? "bg-primary/10" : isNext ? "bg-blue-900/10" : "hover:bg-gray-800/30"}`}
                                  >
                                    <td className="py-3 px-3">
                                      <div
                                        className={`px-3 py-1 rounded-full text-xs font-bold border inline-flex items-center gap-1 ${isCurrent ? "" : isLocked ? "opacity-40" : ""} ${lvlBadgeClass(level)}`}
                                      >
                                        {isCurrent ? (
                                          <span>⭐</span>
                                        ) : isLocked ? (
                                          <LockClosedIcon className="w-2.5 h-2.5" />
                                        ) : (
                                          <CheckCircleIcon className="w-2.5 h-2.5" />
                                        )}
                                        <span>{level}</span>
                                      </div>
                                    </td>
                                    <td
                                      className={`py-3 px-3 text-sm font-medium ${isLocked && !isNext ? "text-gray-600" : "text-white"}`}
                                      style={nw}
                                    >
                                      {name}
                                      {isCurrent && (
                                        <span className="ml-2 text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full border border-primary/30">
                                          Your Tier
                                        </span>
                                      )}
                                      {isNext && !isCurrent && (
                                        <span className="ml-2 text-xs bg-blue-900/40 text-blue-400 px-2 py-0.5 rounded-full border border-blue-800/40">
                                          Next Goal
                                        </span>
                                      )}
                                    </td>
                                    <td
                                      className={`py-3 px-3 text-sm ${isLocked && !isNext ? "text-gray-600" : "text-gray-300"}`}
                                      style={nw}
                                    >
                                      {formatXRP(req.min_volume || 0)} XRP
                                    </td>
                                    <td
                                      className={`py-3 px-3 text-sm font-semibold ${isLocked && !isNext ? "text-gray-700" : "text-yellow-400"}`}
                                      style={nw}
                                    >
                                      {bonus > 0 ? (
                                        `+${formatXRP(bonus)} XRP`
                                      ) : (
                                        <span className="text-gray-600">—</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </Card>
                  )}

                  {/* Recent earnings */}
                  <Card padding="lg">
                    <h3 className={globalStyle.h5}>Recent Earnings</h3>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {[
                        {
                          label: "Today",
                          val: stats.today_earnings || 0,
                          c: "green",
                        },
                        {
                          label: "This Week",
                          val: stats.week_earnings || 0,
                          c: "blue",
                        },
                        {
                          label: "This Month",
                          val: stats.month_earnings || 0,
                          c: "purple",
                        },
                      ].map(({ label, val, c }) => (
                        <div
                          key={label}
                          className="bg-gray-800/30 rounded-lg p-4 text-center"
                        >
                          <p className="text-sm text-gray-400 mb-1">{label}</p>
                          <p className={`text-2xl font-bold text-${c}-500`}>
                            {formatXRP(val)} XRP
                          </p>
                        </div>
                      ))}
                    </div>
                  </Card>
                </>
              )
            )}
          </>
        )}

        {/* ── TEAM TREE TAB ── */}
        {activeTab === "tree" && (
          <Card padding="lg" className="overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h3 className={globalStyle.h5}>Your Team Tree</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {visibleIds
                    ? `Showing ${visibleCount} of ${allFlatNodes.length} members`
                    : `${allFlatNodes.length} total members`}{" "}
                  · Tap any member for details
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {(["tree", "breakdown"] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setTreeSubTab(tab)}
                    style={{
                      padding: "5px 12px",
                      borderRadius: 7,
                      background:
                        treeSubTab === tab
                          ? "rgba(99,102,241,0.2)"
                          : "rgba(255,255,255,0.06)",
                      border:
                        treeSubTab === tab
                          ? "1px solid #818cf8"
                          : "1px solid rgba(255,255,255,0.09)",
                      color: treeSubTab === tab ? "#c7d2fe" : "#9ca3af",
                      fontSize: 11,
                      cursor: "pointer",
                    }}
                  >
                    {tab === "tree" ? "🌳 Tree" : "📊 Breakdown"}
                  </button>
                ))}
                {treeSubTab === "tree" &&
                  (["Expand All", "Collapse", "Refresh"] as const).map(
                    (label) => (
                      <button
                        key={label}
                        onClick={
                          label === "Expand All"
                            ? expandAll
                            : label === "Collapse"
                              ? collapseAll
                              : loadTree
                        }
                        style={{
                          padding: "5px 10px",
                          borderRadius: 7,
                          background: "rgba(255,255,255,0.06)",
                          border: "1px solid rgba(255,255,255,0.09)",
                          color: "#9ca3af",
                          fontSize: 11,
                          cursor: "pointer",
                        }}
                      >
                        {label}
                      </button>
                    ),
                  )}
              </div>
            </div>
            {treeSubTab === "breakdown" ? (
              <VolumeBreakdownTable
                breakdown={breakdown}
                totalVolume={stats?.team_volume ?? 0}
                floorDepth={maxVolDepths}
              />
            ) : loadingTree ? (
              <LoadingScreen message="Loading your full team tree..." />
            ) : referralTree.length === 0 ? (
              <div className="text-center py-16">
                <UserGroupIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">
                  Start inviting friends to build your team!
                </p>
              </div>
            ) : (
              <div
                style={{
                  height: "65vh",
                  minHeight: 320,
                  borderRadius: 10,
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.06)",
                  background:
                    "radial-gradient(ellipse at 20% 20%,rgba(99,102,241,0.05) 0%,transparent 60%)",
                }}
              >
                <LRTree
                  rootNode={rootNode}
                  expanded={expanded}
                  onToggle={toggle}
                  onSelect={(n) => {
                    if (n.id !== -1) {
                      setSelectedNode(n);
                      setShowNodeDetails(true);
                    }
                  }}
                  heatMode={filters.heatMode}
                  heatMap={heatMap}
                  heatTopThreshold={heatTopThreshold}
                  heatMidThreshold={heatMidThreshold}
                  filters={filters}
                  onFiltersChange={setFilters}
                  visibleIds={visibleIds}
                />
              </div>
            )}
            {treeSubTab === "tree" &&
              !loadingTree &&
              referralTree.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-800 flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-gray-500">
                  {filters.heatMode ? (
                    (["top", "mid", "dormant"] as const).map((tier) => (
                      <div key={tier} className="flex items-center gap-1.5">
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: HEAT_PALETTE[tier].border,
                          }}
                        />
                        <span style={{ color: HEAT_PALETTE[tier].text }}>
                          {HEAT_PALETTE[tier].label}
                        </span>
                        <span style={{ color: "#475569" }}>
                          (
                          {tier === "top"
                            ? `top ${topPct}%`
                            : tier === "mid"
                              ? `mid ${midPct}%`
                              : `dormant ${dormPct}%`}
                          )
                        </span>
                      </div>
                    ))
                  ) : (
                    <>
                      {[
                        { color: DEPTH_PALETTE[0].border, label: "You" },
                        { color: DEPTH_PALETTE[1].border, label: "F1" },
                        { color: DEPTH_PALETTE[2].border, label: "F2" },
                        { color: DEPTH_PALETTE[3].border, label: "F3" },
                        { color: DEPTH_PALETTE[4].border, label: "F4" },
                        { color: DEPTH_PALETTE[5].border, label: "F5+" },
                      ].map(({ color, label }) => (
                        <div key={label} className="flex items-center gap-1.5">
                          <div
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: color,
                            }}
                          />
                          <span>{label}</span>
                        </div>
                      ))}
                      <div className="flex items-center gap-1.5">
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: "#22c55e",
                          }}
                        />
                        <span>Active</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div
                          style={{
                            width: 14,
                            height: 8,
                            borderRadius: 3,
                            background: "rgba(245,158,11,0.2)",
                            border: "1px solid rgba(245,158,11,0.4)",
                          }}
                        />
                        <span>Vol weight %</span>
                      </div>
                    </>
                  )}
                </div>
              )}
          </Card>
        )}

        {/* ── EARNINGS TAB ── */}
        {activeTab === "earnings" && (
          <div className="space-y-6">
            {loadingStats ? (
              <LoadingScreen message="Loading earnings..." />
            ) : (
              stats && (
                <>
                  <Card padding="lg">
                    <h3 className={globalStyle.h5}>Earnings Summary</h3>
                    <div className="mt-6">
                      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                        <div>
                          <p className="text-sm text-gray-400">
                            Total Lifetime Earnings
                          </p>
                          <p className="text-3xl sm:text-4xl font-bold text-white">
                            {formatXRP(stats.total_earnings || 0)} XRP
                          </p>
                        </div>
                        <div className="p-4 bg-gradient-to-br from-green-900/30 to-green-900/10 rounded-full">
                          <BanknotesIcon className="w-10 h-10 text-green-500" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                        {[
                          {
                            label: "Today",
                            val: stats.today_earnings,
                            c: "blue",
                          },
                          {
                            label: "This Week",
                            val: stats.week_earnings,
                            c: "green",
                          },
                          {
                            label: "This Month",
                            val: stats.month_earnings,
                            c: "purple",
                          },
                        ].map(({ label, val, c }) => (
                          <div
                            key={label}
                            className={`p-4 bg-${c}-900/20 rounded-xl border border-${c}-800/30`}
                          >
                            <p className={`text-sm text-${c}-300 mb-1`}>
                              {label}
                            </p>
                            <p className="text-2xl font-bold text-white">
                              {formatXRP(val || 0)} XRP
                            </p>
                          </div>
                        ))}
                      </div>
                      {stats.referral_settings && (
                        <div className="bg-gray-800/30 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-300 mb-3">
                            Commission Limits
                          </h4>
                          <div className="space-y-3">
                            {[
                              {
                                label: "Daily",
                                cur: stats.today_earnings,
                                max: stats.referral_settings
                                  .daily_commission_limit,
                                c: "blue",
                              },
                              {
                                label: "Monthly",
                                cur: stats.month_earnings,
                                max: stats.referral_settings
                                  .monthly_commission_limit,
                                c: "purple",
                              },
                            ].map(({ label, cur, max, c }) => (
                              <div key={label}>
                                <div className="flex justify-between mb-1">
                                  <span className="text-xs text-gray-400">
                                    {label} Limit
                                  </span>
                                  <span className="text-xs text-white">
                                    {formatXRP(cur || 0)} / {formatXRP(max)} XRP
                                  </span>
                                </div>
                                <div className="w-full bg-gray-700 rounded-full h-2">
                                  <div
                                    className={`bg-${c}-500 h-2 rounded-full`}
                                    style={{
                                      width: `${Math.min(((cur || 0) / max) * 100, 100)}%`,
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                  <Card padding="lg">
                    <h3 className={globalStyle.h5}>
                      🚀 Maximize Your Earnings
                    </h3>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {[
                        {
                          icon: UserPlusIcon,
                          bg: "blue",
                          title: "Refer Direct Friends",
                          desc: "F1 referrals count at 100% — highest weight, highest impact",
                        },
                        {
                          icon: PresentationChartLineIcon,
                          bg: "green",
                          title: "Build Deep Networks",
                          desc: `Volume extends to unlimited depth at the floor rate (${(floorWeight * 100).toFixed(0)}%)`,
                        },
                        {
                          icon: GiftIcon,
                          bg: "purple",
                          title: "Level-Up Tier Bonuses",
                          desc: "Grow weighted volume to unlock one-time XRP bonuses. Progress resets each tier.",
                        },
                        {
                          icon: TrophyIcon,
                          bg: "yellow",
                          title: "Monthly Prize Pool",
                          desc: "Top 3 referrers by weighted volume win bonus XRP each month",
                        },
                      ].map(({ icon: Icon, bg, title, desc }) => (
                        <div
                          key={title}
                          className="flex items-start gap-3 p-3 bg-gray-800/30 rounded-lg"
                        >
                          <div
                            className={`w-10 h-10 rounded-full bg-${bg}-900 flex items-center justify-center flex-shrink-0`}
                          >
                            <Icon className={`w-5 h-5 text-${bg}-300`} />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">
                              {title}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">{desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                </>
              )
            )}
          </div>
        )}
      </main>
      <BottomNav />

      {/* How It Works Modal */}
      <Modal
        isOpen={showHowItWorks}
        onClose={() => setShowHowItWorks(false)}
        title="How Referrals Work"
      >
        <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-2">
          <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-xl p-5 border border-blue-800/30">
            <h3 className="text-base font-semibold text-white mb-2">
              🌳 Weighted Team Volume (Infinite Depth)
            </h3>
            <p className="text-sm text-gray-300">
              Every member at every depth contributes to your volume. Deeper
              members contribute at reduced rates, but never 0. The rate floors
              at{" "}
              <strong className="text-yellow-400">
                {(floorWeight * 100).toFixed(0)}%
              </strong>{" "}
              for any depth beyond F{maxVolDepths}.
            </p>
          </div>
          <div
            style={{
              background: "rgba(249,115,22,0.06)",
              border: "1px solid rgba(249,115,22,0.2)",
              borderRadius: 12,
              padding: "12px 16px",
            }}
          >
            <h3 className="text-sm font-semibold text-white mb-2">
              📊 Total Sales vs Weighted Volume
            </h3>
            <p className="text-xs text-gray-300">
              Total Sales = the raw unweighted sum of all your downline's
              deposits across unlimited depths. Weighted Volume = what actually
              counts toward your tier progression (applies per-depth weights).
            </p>
          </div>
          <div
            style={{
              background: "rgba(245,158,11,0.06)",
              border: "1px solid rgba(251,191,36,0.2)",
              borderRadius: 12,
              padding: "14px 16px",
            }}
          >
            <p
              style={{
                fontSize: 12,
                color: "#fde68a",
                fontWeight: 600,
                marginBottom: 10,
              }}
            >
              📐 Example Calculation
            </p>
            {[1, 2, 3].map((depth) => {
              const weight = weightForDepth(depth, depthWeights, floorWeight);
              const deposit = [5000, 3000, 2000][depth - 1];
              const pal = dp(depth);
              return (
                <div
                  key={depth}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      border: `2px solid ${pal.border}`,
                      background: pal.bg,
                      color: pal.text,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 9,
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    F{depth}
                  </div>
                  <span style={{ fontSize: 11, color: "#93c5fd" }}>
                    {deposit.toLocaleString()} XRP
                  </span>
                  <span style={{ fontSize: 11, color: "#64748b" }}>×</span>
                  <span
                    style={{ fontSize: 11, color: "#fde68a", fontWeight: 700 }}
                  >
                    {(weight * 100).toFixed(0)}%
                  </span>
                  <span style={{ fontSize: 11, color: "#64748b" }}>=</span>
                  <span
                    style={{ fontSize: 12, fontWeight: 700, color: "#fbbf24" }}
                  >
                    {formatXRP(deposit * weight)} XRP
                  </span>
                </div>
              );
            })}
            <div
              style={{
                borderTop: "1px solid rgba(255,255,255,0.07)",
                marginTop: 8,
                paddingTop: 8,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontSize: 11, color: "#64748b" }}>
                Weighted total:
              </span>
              <span style={{ fontSize: 13, fontWeight: 800, color: "#fbbf24" }}>
                {formatXRP(
                  [1, 2, 3].reduce(
                    (s, d) =>
                      s +
                      [5000, 3000, 2000][d - 1] *
                        weightForDepth(d, depthWeights, floorWeight),
                    0,
                  ),
                )}{" "}
                XRP
              </span>
            </div>
          </div>
          <div
            style={{
              background: "rgba(99,102,241,0.06)",
              border: "1px solid rgba(99,102,241,0.2)",
              borderRadius: 12,
              padding: "12px 16px",
            }}
          >
            <h3 className="text-sm font-semibold text-white mb-2">
              🏆 Tier Progress — Resets Per Level
            </h3>
            <p className="text-xs text-gray-300">
              When you achieve a new tier badge, your progress counter resets to
              0 XRP. You only need to accumulate the volume difference between
              the current and next tier threshold — not the full next-tier
              amount from zero.
            </p>
          </div>
          <div className="bg-gray-800/30 rounded-lg p-4">
            <h4 className="text-sm font-medium text-white mb-3">
              🔥 Heat Map Tiers
            </h4>
            {(["top", "mid", "dormant"] as const).map((tier) => (
              <div key={tier} className="flex items-center gap-3 mb-2">
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: HEAT_PALETTE[tier].border,
                    flexShrink: 0,
                    display: "inline-block",
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    color: HEAT_PALETTE[tier].text,
                    fontWeight: 600,
                  }}
                >
                  {HEAT_PALETTE[tier].label}
                </span>
                <span className="text-xs text-gray-500">
                  {tier === "top"
                    ? `Top ${topPct}% by weighted contribution`
                    : tier === "mid"
                      ? `Next ${midPct}% (mid performers)`
                      : `Bottom ${dormPct}% — consider engaging them`}
                </span>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* Node Detail Modal */}
      <Modal
        isOpen={showNodeDetails}
        onClose={() => setShowNodeDetails(false)}
        title={
          selectedNode?.id === -1
            ? "Your Profile"
            : `@${selectedNode?.username}`
        }
        size="md"
      >
        {selectedNode && (
          <NodeDetailContent
            node={selectedNode}
            myUsername={myUsername}
            heatTier={
              filters.heatMode ? heatMap.get(selectedNode.id) : undefined
            }
            onClose={() => setShowNodeDetails(false)}
          />
        )}
      </Modal>
    </div>
  );
};

export default Referral;
